import { Controller } from "@hotwired/stimulus"

// Handles optimistic updates and state transitions inside the checkout address modal.
export default class extends Controller {
  static targets = [
    "backdrop",
    "dialog",
    "formSection",
    "listSection",
    "addButton",
    "emptyList",
    "form",
    "card",
    "zoneSearch",
    "zoneSuggestion",
    "zoneNoResults"
  ]

  static values = {
    customer: Boolean,
    hasSelection: Boolean
  }

  connect() {
    this.submitStartHandler = this.handleSubmitStart.bind(this)
    this.submitEndHandler = this.handleSubmitEnd.bind(this)

    this.element.addEventListener("turbo:submit-start", this.submitStartHandler)
    this.element.addEventListener("turbo:submit-end", this.submitEndHandler)

    this.filterZoneSuggestions()
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-start", this.submitStartHandler)
    this.element.removeEventListener("turbo:submit-end", this.submitEndHandler)
  }

  prefillNewForm(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.showFormSection()
    this.ensureModalVisible()
  }

  close(event) {
    if (event) event.preventDefault()
    this.hideModal()
  }

  handleSubmitStart(event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return

    const action = form.dataset.addressModalAction
    if (!action) return

    this.pendingAction = action

    switch (action) {
      case "select":
        this.optimisticSelect(form)
        break
      case "create":
        this.optimisticCreate(form)
        break
      case "delete":
        this.optimisticDelete(form)
        break
    }
  }

  handleSubmitEnd(event) {
    const success = event.detail ? event.detail.success : false

    if (!success && (this.pendingAction === "select" || this.pendingAction === "create")) {
      this.showModal()
    }

    this.pendingAction = null
  }

  optimisticSelect(form) {
    const card = form.closest("[data-address-modal-address]")
    if (card) {
      const address = this.parseAddress(card.dataset.addressModalAddress)
      if (address) {
        this.dispatchAddressSelected(address)
      }
    }

    this.hideModal()
  }

  optimisticCreate(form) {
    const address = this.buildAddressFromForm(form)
    if (address) {
      this.dispatchAddressSelected(address)
    }

    this.hideModal()
  }

  optimisticDelete(form) {
    const card = form.closest("[data-address-modal-address]")
    if (!card) return

    const isCurrent = card.dataset.addressModalCurrent === "true"
    card.remove()

    if (isCurrent) {
      this.dispatchAddressCleared()
    }

    this.toggleEmptyState()

    if (this.cardTargets.length === 0) {
      this.showFormSection()
    }
  }

  buildAddressFromForm(form) {
    const data = new FormData(form)
    const pick = (key) => {
      const value = data.get(`address[${key}]`)
      return typeof value === "string" ? value.trim() : value
    }

    const isCompany = this.parseBoolean(pick("is_company"))

    const address = {
      id: pick("id") || null,
      line1: pick("line1"),
      line2: pick("line2"),
      suburb: pick("suburb"),
      city: pick("city"),
      province: pick("province"),
      postal_code: pick("postal_code"),
      country: pick("country"),
      is_company: isCompany,
      company_name: pick("company_name"),
      vat_number: pick("vat_number")
    }

    // Ignore entirely empty payloads (e.g. submission without Google fill yet).
    const hasContent = Object.values(address).some((value) => value && `${value}`.length > 0)
    return hasContent ? address : null
  }

  showFormSection() {
    if (this.hasFormSectionTarget) {
      this.formSectionTarget.classList.remove("hidden")
    }

    if (this.hasListSectionTarget) {
      this.listSectionTarget.classList.add("hidden")
    }

    this.toggleAddButton(false)

    if (this.hasFormTarget) {
      const modeInput = this.formTarget.querySelector("input[name='address[modal_mode]']")
      if (modeInput) modeInput.value = "new"
    }
  }

  ensureModalVisible() {
    this.showModal()
  }

  hideModal() {
    const modal = this.customModalController
    if (modal?.closeCustomModal) {
      modal.closeCustomModal()
      return
    }

    if (this.hasDialogTarget) {
      this.dialogTarget.classList.add("hidden")
    }
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden")
    }
  }

  showModal() {
    const modal = this.customModalController
    if (modal?.openCustomModal) {
      modal.openCustomModal()
      return
    }

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("hidden")
    }
    if (this.hasDialogTarget) {
      this.dialogTarget.classList.remove("hidden")
    }
  }

  toggleAddButton(visible) {
    if (!this.hasAddButtonTarget) return

    this.addButtonTarget.classList.toggle("hidden", !visible)
  }

  toggleEmptyState() {
    if (!this.hasEmptyListTarget) return

    const shouldShow = this.cardTargets.length === 0
    this.emptyListTarget.classList.toggle("hidden", !shouldShow)
    this.toggleAddButton(!shouldShow)
  }

  filterZoneSuggestions(event) {
    if (!this.hasZoneSuggestionTarget) return

    const rawTerm = event?.target?.value ?? (this.hasZoneSearchTarget ? this.zoneSearchTarget.value : "")
    const searchTerm = `${rawTerm}`.trim().toLowerCase()

    if (searchTerm.length === 0) {
      this.zoneSuggestionTargets.forEach((card) => this.toggleZoneSuggestion(card, true))
      this.toggleZoneNoResults(false)
      return
    }

    let visibleCount = 0

    this.zoneSuggestionTargets.forEach((card) => {
      const searchableText = [
        card.dataset.zoneSuburb,
        card.dataset.zoneTown,
        card.dataset.zonePostalCode
      ].join(" ").toLowerCase()

      const visible = searchableText.includes(searchTerm)
      this.toggleZoneSuggestion(card, visible)
      if (visible) visibleCount += 1
    })

    this.toggleZoneNoResults(visibleCount === 0)
  }

  toggleZoneSuggestion(card, visible) {
    card.classList.toggle("hidden", !visible)

    if (!visible) {
      const selectedRadio = card.querySelector("input[type='radio'][name='selected_zone_id']")
      if (selectedRadio?.checked) {
        selectedRadio.checked = false
      }
    }
  }

  toggleZoneNoResults(show) {
    if (!this.hasZoneNoResultsTarget) return
    this.zoneNoResultsTarget.classList.toggle("hidden", !show)
  }

  get customModalController() {
    if (this._customModalController && document.body.contains(this._customModalController.element)) {
      return this._customModalController
    }

    this._customModalController = this.application.getControllerForElementAndIdentifier(this.element, "custom-modal")
    return this._customModalController
  }

  parseAddress(payload) {
    if (!payload) return null
    try {
      const parsed = JSON.parse(payload)
      if (parsed && typeof parsed === "object") {
        return parsed
      }
    } catch (error) {
      console.error("Failed to parse address payload", error)
    }
    return null
  }

  dispatchAddressSelected(address) {
    const normalised = this.normaliseAddress(address)
    window.dispatchEvent(
      new CustomEvent("checkout-address:selected", {
        detail: { address: normalised }
      })
    )
  }

  dispatchAddressCleared() {
    window.dispatchEvent(new CustomEvent("checkout-address:cleared"))
  }

  normaliseAddress(address) {
    const data = { ...address }
    if (data.postalCode && !data.postal_code) data.postal_code = data.postalCode
    if (!data.postalCode && data.postal_code) data.postalCode = data.postal_code
    return data
  }

  parseBoolean(value) {
    if (value === true || value === false) return value
    if (value == null) return null

    const normalized = `${value}`.trim().toLowerCase()
    if (["true", "1", "yes", "on"].includes(normalized)) return true
    if (["false", "0", "no", "off"].includes(normalized)) return false
    return null
  }
}
